import React, { Component, setGlobal, Fragment } from "reactn";
import styled from "styled-components";
import {
    Dialog,
    Button,
    IconButton,
    Icon,
    DialogTitle,
    TextField,
    DialogActions,
} from "@material-ui/core";

import { FlexBox } from "js/react/components/LayoutGrid";
import {
    BeautifulDialog,
    DialogContent, ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowMessageDialog,
    ShowUpgradeDialog
} from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { BlueButton } from "js/react/components/UiComponents";
import ProBadge from "js/react/components/ProBadge";
import { themeColors } from "js/react/sharedStyles";
import { PresentationPrivacyType } from "js/core/models/presentation";
import { app } from "js/namespaces";
import { FeatureType } from "js/core/models/features";
import { trackActivity } from "js/core/utilities/utilities";
import shouldShowProBadge from "js/editor/shouldShowProBadge";
import linkDS, { LinkType } from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import { incUserProps } from "js/analytics";
import { UIController } from "js/editor/dialogs/UIController";
import Api from "js/core/api";
import Loadable from "js/react/components/Loadable";
import { _ } from "../../../../vendor";
import permissionsDS from "../dataservices/PermissionsDataService";
import log from "../../../../core/logger";
import TextArea from "js/react/components/TextArea";
import { UpgradePlanDialogType } from "../../MarketingDialogs/UpgradePlanDialog";
import PresentationSettingsContainer from "../PresentationSettingsContainer";
import PermissionsList from "../panes/PermissionsList";
import { WarningWatermark } from "js/react/components/WarningWatermark";
import LinkExpiration from "js/react/components/LinkExpiration";
import { ToggleAllowCommenting } from "../components/ToggleAllowCommenting";
import { InviteTagsInput } from "js/react/components/InviteInput/InviteTagsInput";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { ConfirmInviteDialog } from "js/react/components/InviteInput/ConfirmInviteDialog";
import { normalizePermissionForDisplay } from "js/core/utilities/permissions";

const ShareDialogContainer = styled(Dialog)`
  .MuiPaper-root {
    display: grid;
    row-gap: 10px;
    grid-auto-flow: row;
    background-color: transparent;
  }
  
  .MuiOutlinedInput-root.Mui-disabled {
    height: 44px;
    pointer-events: none;
  }
  
  .MuiDialogTitle-root{
    padding: 24px 34px 20px 34px;
    .MuiIcon-root{
        font-size: 34px;
        color: #11a9ef;
        vertical-align: -9px;
        margin-right: 10px;
    }
  }
  
  .dialog-content {
    padding-bottom: 14px;
  }
`;

const DialogPanel = styled.div`
  background: white;
  border-radius: 4px;
  padding: 0px;
  max-width: min(100vw - 20px, 860px);
`;

const Expander = styled.div`
  transition: 300ms;
  overflow: hidden;
  height: auto;
`;

export class ShareDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedPanel: props.selectedPanel || "link",
        };
    }

    handleSetSelected = selectedPanel => {
        this.setState({
            selectedPanel,
        });
    }

    render() {
        let { selectedPanel } = this.state;

        return (
            <ShareDialogContainer
                open
                className="share-dialog-container"
                maxWidth="md"
                onEscapeKeyDown={event => {
                    event.stopPropagation();
                    this.props.closeDialog();
                }}
                onBackdropClick={() => {
                    this.props.closeDialog();
                }}
            >
                {
                    app.isConstrained &&
                    <FlexBox right>
                        <IconButton className="exit-button" onClick={this.props.closeDialog}>
                            <Icon>close</Icon>
                        </IconButton>
                    </FlexBox>
                }
                <CollaboratePanel
                    emails={this.props.emails}
                    location={this.props.location || "simple-share"}
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "collaborate"}
                    onSelected={() => this.handleSetSelected("collaborate")}
                    closeDialog={this.props.closeDialog}
                />
                <ShareLinkPanel
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "link"}
                    onSelected={() => this.handleSetSelected("link")}
                    closeDialog={this.props.closeDialog}
                />
                <SecuredInvitePanel
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "invite"}
                    onSelected={() => this.handleSetSelected("invite")}
                    closeDialog={this.props.closeDialog}
                />
            </ShareDialogContainer>
        );
    }
}

class ShareLinkPanel extends Component {
    state = {
        link: null,
        linkExpiresAt: null,
        linkShouldExpire: false,
        linkHasExpired: false,
    }

    constructor() {
        super();
        this.linkRef = React.createRef();
    }

    componentDidMount() {
        if (!this.props.presentation.links) {
            this.props.presentation.loadPresentationLinks();
        }

        let link = this.props.presentation.links.find(link => link.get("isDefault") && link.get("type") == "public");

        if (link) {
            this.setState({
                link,
                linkExpiresAt: link.get("expiresAt")
            });
        } else {
            linkDS.createPublicLink(this.props.presentation)
                .then(link => {
                    link.update({ isDefault: true });
                    this.setState({ link: link, shouldCleanup: true });
                })
                .catch(err => {
                    console.error(err);
                });
        }
    }

    componentWillUnmount() {
        if (!this.state.linkCopied && this.state.link && this.state.shouldCleanup) {
            this.state.link.destroy();
        }
    }

    handleAccept = async () => {
        let { presentation } = this.props;
        let { link } = this.state;

        if (!link.get("enabled")) {
            link.update({ enabled: true });
        }

        // set the presentation to PUBLIC if necessary
        if (presentation.getPrivacySetting() !== PresentationPrivacyType.PUBLIC) {
            if (!(await ShowConfirmationDialog({
                title: "Make this presentation public?",
                message: <>
                    <p>In order to share your presentation, its privacy setting must be set to <b>Public.</b></p>
                    <br/>
                    <p>Public presentations can only be viewed by people who have been given a link or invited to the presentation.</p>
                </>
            }))) {
                return;
            }

            presentation.setPrivacySetting(PresentationPrivacyType.PUBLIC);

            const eventProps = {
                presentation_id: presentation.get("id"),
                old_value: presentation.getPrivacySetting(),
                new_value: PresentationPrivacyType.PUBLIC
            };
            trackActivity("Sharing", "ChangePrivacySetting", null, null, eventProps, true);
        }

        await linkDS.saveLink(presentation, link);
        this.setState({ linkCopied: true });

        const shareSuccessful = url => {
            this.props.closeDialog(url);
            const props = {
                presentation_id: presentation.id,
                link_ids: [link.get("id")],
                is_new: true,
                type: "public_link",
                workspace_id: presentation.getWorkspaceId()
            };
            trackActivity("Sharing", "CopyLink", null, null, props, { audit: true });
        };

        // Try to share. If it's cancelled or unsupported, we do not continue tracking
        try {
            let url = link.getLinkURL();
            if (app.isMobileOrTablet && window.navigator.share) {
                await window.navigator.share({ url });
                shareSuccessful(url);
            } else
            if (window.navigator.clipboard) {
                await window.navigator.clipboard.writeText(url);
                shareSuccessful(url);
            }
        } catch (error) {
            console.log("[Share Canceled]", error);
        }
    };

    toggleAllowCommenting = event => {
        let { link } = this.state;
        if (link) {
            const allowCommenting = !link.attributes.allowCommenting;
            link.update({ allowCommenting });
        }
    }

    handleUpdateLinkExpiration = ({
        linkExpiresAt,
        linkShouldExpire,
        linkHasExpired,
    }) => {
        this.setState({
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
        });

        if (!linkHasExpired) {
            this.state.link.update({
                expiresAt: linkShouldExpire ? linkExpiresAt : null
            });
        }
    }

    handleShowMoreOptions = () => {
        this.props.closeDialog();
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        ShowDialog(PresentationSettingsContainer, { startPane: "share" });
    }

    render() {
        let {
            expanded,
            presentation,
        } = this.props;
        let {
            link,
            linkExpiresAt,
            linkHasExpired,
        } = this.state;

        const useShareIcon = app.isMobileOrTablet && window.navigator.share;
        const disabled = !link || linkHasExpired || (link?.shouldLinkExpire() && link?.isLinkExpired());

        const allowCommenting = link?.attributes.allowCommenting;

        return (
            <DialogPanel onClick={this.props.onSelected}>
                <DialogTitle><Icon>link</Icon>Share Public Link</DialogTitle>
                <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                    <DialogContent>
                        <h3>
                            Anyone can use this link to view this presentation.
                        </h3>
                        {
                            !app.isConstrained &&
                            <Gap20/>
                        }
                        <FlexBox>
                            {
                                !app.isConstrained &&
                                <Fragment>
                                    <FlexSpacer>
                                        <TextField
                                            inputRef={this.linkRef}
                                            disabled
                                            value={link ? link.getLinkURL() : "Generating link..."}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    </FlexSpacer>
                                    <Gap10/>
                                </Fragment>
                            }
                            {
                                !useShareIcon &&
                                <BlueButton onClick={this.handleAccept} disabled={disabled}>
                                    Copy Link
                                </BlueButton>
                            }
                            {
                                useShareIcon &&
                                <BlueButton onClick={this.handleAccept} disabled={disabled}>
                                    <Icon>ios_share</Icon>
                                </BlueButton>
                            }
                        </FlexBox>

                        <Gap20 />

                        <ToggleAllowCommenting
                            allowCommenting={allowCommenting}
                            onToggle={this.toggleAllowCommenting}
                        />

                        <LinkExpiration
                            expiresAt={linkExpiresAt}
                            presentation={presentation}
                            onUpdate={this.handleUpdateLinkExpiration}
                        />

                    </DialogContent>
                    <WarningWatermark
                        cta="GetLinkWarningWatermark"
                        presentation={this.props.presentation}
                    />
                    {
                        !app.isConstrained && app.mainView.editorView &&
                        <DialogActions>
                            <Button onClick={this.handleShowMoreOptions}>More Sharing Options...</Button>
                            <FlexSpacer/>
                        </DialogActions>
                    }
                </Expander>
            </DialogPanel>
        );
    }
}

export class SecuredInvitePanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            inviteContext: null,
            customMessage: "",
            allowCommenting: false,
            linkExpiresAt: null,
            linkShouldExpire: false,
            linkHasExpired: false,
        };
    }

    componentDidMount = async () => {
        let {
            presentation,
        } = this.props;

        if (!presentation.links?.loadPromise) {
            await presentation.links.loadModels();
        } else {
            await presentation.links.loadPromise;
        }
        let links = _.filter(presentation.links.models, link => link.get("type") == LinkType.INVITE);
        let oldSuggestions = links.map(link => link.get("email"));

        let inviteContext = createInviteSuggestionContext({
            includeFolders: false,
            allowOldSelection: true,
            presentationId: presentation.id,
            oldSuggestions,
            saveContext: inviteContext => this.setState({ inviteContext }),
        });

        this.setState({
            inviteContext,
        });
    }

    componentWillUnmount() {
        this.state.inviteContext.destroy();
    }

    handlePreviewEmail = async () => {
        ShowDialog(InvitePreviewEmailDialog, {
            presentation: this.props.presentation,
            customMessage: this.state.customMessage
        });
    };

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    };

    handleAccept = async () => {
        let {
            presentation,
        } = this.props;

        let {
            inviteContext,
            customMessage,
            linkExpiresAt,
            allowCommenting,
        } = this.state;

        if (!presentation.get("secured") && presentation.get("public")) {
            if (!(await ShowConfirmationDialog({
                title: "Your presentation is currently public",
                message: <Fragment>
                    <p>In order to send a secured invite, your presentation's security setting will be set to <b>Secured.</b></p>
                    <p>Any existing public links or embeds for this presentation will be disabled.</p>
                    <br/>
                    <b>Are you sure you wish to continue?</b>
                </Fragment>
            }))) {
                return;
            }
        }

        let emails = null;

        try {
            // We're calling getSelectionWithRedundanciesRemoved() instead of getSelectedUniqueUsers() because
            //   we are not suggesting folders and we want to to ensure non-redundant invitations
            let users = inviteContext.getSelectionWithRedundanciesRemoved();

            await ShowDialogAsync(ConfirmInviteDialog, {
                title: "Invite the following viewers?",
                includeSubheader: false,
                hasPermissionEditing: false,
                users,
                callback: async permission => {
                    emails = users.map(user => user.email);

                    let asyncInvites = emails.map(email => {
                        return linkDS.createInviteLink(
                            presentation,
                            email,
                            customMessage,
                            linkExpiresAt,
                            allowCommenting
                        )
                            .then(link => {
                                return linkDS
                                    .saveLink(presentation, link)
                                    .then(() => link.id);
                            });
                    });

                    let linkIds = await Promise.all(asyncInvites);

                    const props = {
                        presentation_id: presentation.id,
                        link_ids: linkIds,
                        is_new: true,
                        type: "email",
                        sender_email: app.user.getEmail(),
                        recipient_emails: emails,
                        workspace_id: presentation.getWorkspaceId()
                    };
                    trackActivity("Sharing", "EmailLink", null, null, props, { audit: true });

                    if (!presentation.get("secured")) {
                        await presentation.setPrivacySetting(PresentationPrivacyType.SECURED);
                        ShowMessageDialog({
                            title: "Secured invites were sent successfully!",
                            message: "Your presentation's security setting has been set to Secured."
                        });
                    } else {
                        ShowMessageDialog({
                            title: "Secured invites were sent successfully!",
                        });
                    }
                },
            });
        } catch (err) {
            ShowErrorDialog({
                error: "Error while sharing",
                message: err.message
            });
            console.error(err);
        } finally {
            this.props.closeDialog(emails);
        }
    };

    toggleAllowCommenting = event => {
        const allowCommenting = !this.state.allowCommenting;
        this.setState({ allowCommenting });
    }

    handleUpdateLinkExpiration = ({
        linkExpiresAt,
        linkShouldExpire,
        linkHasExpired,
    }) => {
        this.setState({
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
        });
    }

    render() {
        let {
            expanded,
            presentation,
            onSelected,
        } = this.props;
        const {
            inviteContext,
            customMessage,
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
            allowCommenting,
        } = this.state;

        const isSendDisabled = (
            !app.user.features.isFeatureEnabled(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()) ||
            !inviteContext?.selection.length ||
            (
                linkShouldExpire &&
                linkHasExpired
            )
        );

        return (
            <DialogPanel onClick={onSelected}>
                <DialogTitle>
                    <FlexBox left middle>
                        <Icon>lock</Icon>
                        Share Securely
                        <ProBadge
                            show={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                            analytics={{ cta: "SecuredSharing", ...this.props.presentation.getAnalytics() }}
                            workspaceId={UIController.getWorkspaceId()}
                        />
                    </FlexBox>
                </DialogTitle>
                <Expander style={{ maxHeight: expanded ? "600px" : "0px" }}>
                    {
                        !app.isConstrained &&
                        <Fragment>
                            <DialogContent>
                                <h3>
                                    We'll send each person an email with an individual link to view this presentation.
                                    Pro users can track engagement with comments and viewer analytics.
                                </h3>
                                <Gap20/>
                                {
                                    inviteContext &&
                                    <InviteTagsInput
                                        inviteContext={inviteContext}
                                        sourceName="SecuredInvitePanel"
                                        inputProps={{
                                            readOnly: shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()),
                                            onClick: () => {
                                                if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                    ShowUpgradeDialog({
                                                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                        analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                        workspaceId: UIController.getWorkspaceId()
                                                    });
                                                }
                                            }
                                        }}
                                    />
                                }
                                <Gap10/>
                                <TextArea
                                    readOnly={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                                    isVisible
                                    text={customMessage}
                                    onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                    placeholder="Add a note..."
                                    inputProps={{
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap20 />
                                <ToggleAllowCommenting
                                    allowCommenting={allowCommenting}
                                    onToggle={this.toggleAllowCommenting}
                                />

                                <LinkExpiration
                                    expiresAt={linkExpiresAt}
                                    presentation={presentation}
                                    onUpdate={this.handleUpdateLinkExpiration}
                                />

                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.handlePreviewEmail}>Preview Email</Button>
                                <FlexSpacer/>
                                <BlueButton disabled={isSendDisabled} onClick={this.handleAccept} color="primary">
                                    Send Email
                                </BlueButton>
                            </DialogActions>
                        </Fragment>
                    }
                    {
                        app.isConstrained &&
                        <Fragment>
                            <DialogContent>
                                {
                                    inviteContext &&
                                    <InviteTagsInput
                                        inviteContext={inviteContext}
                                        sourceName="SecuredInvitePanel"
                                        inputProps={{
                                            readOnly: shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()),
                                            onClick: () => {
                                                if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                    ShowUpgradeDialog({
                                                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                        analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                        workspaceId: UIController.getWorkspaceId()
                                                    });
                                                }
                                            }
                                        }}
                                    />
                                }
                                <Gap10/>
                                <TextArea
                                    readOnly={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                                    isVisible
                                    text={customMessage}
                                    onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                    placeholder="Add a note..."
                                    inputProps={{
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap20/>
                                <LinkExpiration
                                    expiresAt={linkExpiresAt}
                                    presentation={presentation}
                                    onUpdate={this.handleUpdateLinkExpiration}
                                />

                                <Gap20/>
                                <h3 style={{ margin: 0 }}>
                                    Recipients will need to verify their identity to view this presentation.
                                </h3>
                            </DialogContent>
                            <DialogActions
                                style={{
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}
                            >
                                <BlueButton disabled={isSendDisabled} onClick={this.handleAccept} color="primary">
                                    Send Email
                                </BlueButton>
                                <Gap20/>
                                <Button
                                    className="text-blue"
                                    onClick={this.handlePreviewEmail}
                                >Preview Email</Button>
                            </DialogActions>
                        </Fragment>
                    }
                </Expander>
            </DialogPanel>
        );
    }
}

export class InvitePreviewEmailDialog extends Component {
    state = {
        isLoading: true
    }

    async componentDidMount() {
        const response = await Api.renderPresentationLinkEmail.post({
            presentationId: this.props.presentation.id,
            customMessage: this.props.customMessage
        });

        this.setState({
            isLoading: false,
            previewHTML: response.html
        });
    }

    render() {
        let { isLoading, previewHTML } = this.state;

        return (
            <BeautifulDialog maxWidth="lg" fullWidth={false} closeButton={app.isConstrained} closeDialog={this.props.closeDialog}>
                <Loadable isLoading={isLoading}>
                    <DialogContent
                        dangerouslySetInnerHTML={{ __html: previewHTML }}
                    ></DialogContent>
                </Loadable>
            </BeautifulDialog>
        );
    }
}

const TeamGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-height: 115px;
  overflow-y: auto;
  button {
    height: 32px;
    margin-right: 10px;
    margin-bottom: 10px;
  }
`;

const TeamCTA = styled.div`
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  a {
    color: ${themeColors.ui_blue} ;
    cursor: pointer;
  } 
`;

class CollaboratePanel extends Component {
    constructor(props) {
        super(props);

        // Retrieve workspace prohibited flags
        let prohibitExternalWorkspaceCollaboration = app.user.features.isFeatureEnabled(
            FeatureType.PROHIBIT_EXTERNAL_WORKSPACE_COLLABORATION,
            app.appState.orgId
        );

        let inviteContext = createInviteSuggestionContext({
            includeFolders: true,
            presentationId: this.props.presentation.id,
            prohibitExternalWorkspaceCollaboration,
            saveContext: inviteContext => this.setState({ inviteContext }),
            afterInit: () => {
                let emails = props.emails || [];
                this.state.inviteContext.selectItems(emails);
            },
        });

        this.state = {
            inviteContext,
            customMessage: "",
            permission: "write",
            isLoading: false,
            showCollaboratorList: false,
        };
    }

    componentWillUnmount() {
        this.state.inviteContext.destroy();
    }

    handleToggleCollaboratorList = showCollaboratorList => {
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        this.setState({
            showCollaboratorList: showCollaboratorList,
        });
    }

    handleViewAllCollaborators = () => {
        this.props.closeDialog();
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        ShowDialog(PresentationSettingsContainer, { startPane: "collaborate" });
    }

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    }

    handleDropdownChange = value => {
        this.setState({ permission: value });
    }

    handleAccept = async () => {
        if (!this.state.isLoading) {
            const {
                inviteContext,
                customMessage,
            } = this.state;
            const { location } = this.props;
            this.setState({ isLoading: true });
            try {
                let users = inviteContext.getSelectedUniqueUsers();
                let selection = inviteContext.getSelectionWithRedundanciesRemoved();
                let presentationId = this.props.presentation.id;

                await ShowDialogAsync(ConfirmInviteDialog, {
                    hasPermissionEditing: true,
                    users,
                    callback: async permission => {
                        let emails = [];
                        let folders = [];
                        let asyncInvites = [];

                        let displayPerm = normalizePermissionForDisplay(permission);

                        selection.forEach(item => {
                            switch (item.type) {
                                case "folder":
                                    // Skip folders that wouldn't be changing permissions
                                    if (item.permissionType !== displayPerm) {
                                        folders.push({
                                            id: item.id,
                                            name: item.name,
                                        });
                                        asyncInvites.push(permissionsDS.inviteTeam(item.id, permission, presentationId, false));
                                    }
                                    break;
                                case "user":
                                default:
                                    emails.push(item.email);
                                    break;
                            }
                        });

                        // If we have individual user emails, give them permissions separate from the teams
                        if (emails.length) {
                            const payload = { emails, permission, customMessage };
                            asyncInvites.push(permissionsDS.createPermission(payload, presentationId, false));
                        }

                        await Promise.all(asyncInvites);

                        incUserProps({
                            invites: emails.length
                        });
                        const props = {
                            presentation_id: presentationId,
                            old_value: null,
                            new_value: permission,
                            team_folders: folders,
                            recipient_emails: emails,
                            sender_email: app.user.getEmail(),
                            location
                        };

                        trackActivity("Collab", "Invite", null, null, props, { audit: true });
                        this.props.closeDialog(selection);
                    },
                });
            } catch (err) {
                log.error(err);
                ShowErrorDialog({
                    error: "Failed to collaborate",
                    message: err.message
                });
            } finally {
                this.setState({ isLoading: false });
            }
        }
    };

    render() {
        let { expanded, presentation } = this.props;
        const {
            inviteContext,
            customMessage,
            isLoading,
            showCollaboratorList
        } = this.state;

        return (
            <Fragment>
                {
                    !showCollaboratorList &&
                    <DialogPanel onClick={this.props.onSelected}>
                        <DialogTitle><Icon>person_add_alt_1</Icon>Invite collaborators</DialogTitle>
                        <Loadable isLoading={isLoading}>
                            <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                                <DialogContent>
                                    <InviteTagsInput
                                        inviteContext={inviteContext}
                                        sourceName="CollaboratePanel"
                                    />

                                    <Gap10/>
                                    <TextArea
                                        isVisible
                                        text={customMessage}
                                        onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                        placeholder="Add a note..."
                                    />
                                </DialogContent>
                                {
                                    !app.isConstrained &&
                                    <DialogActions>
                                        {app.mainView.editorView && <Button onClick={this.handleViewAllCollaborators}>View all collaborators</Button>}
                                        <FlexSpacer/>
                                        <BlueButton disabled={!inviteContext.selection.length} onClick={this.handleAccept} color="primary">
                                            Send Invite
                                        </BlueButton>
                                    </DialogActions>
                                }
                                {
                                    app.isConstrained &&
                                    <DialogActions
                                        style={{
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <BlueButton size="large" disabled={!inviteContext.selection.length} onClick={this.handleAccept} color="primary">
                                            Send Invite
                                        </BlueButton>
                                        <Gap10/>
                                        {app.mainView.editorView && <Button
                                            className="text-blue"
                                            onClick={() => this.handleToggleCollaboratorList(true)}
                                        >View all collaborators</Button>}
                                    </DialogActions>
                                }
                            </Expander>
                        </Loadable>
                    </DialogPanel>
                }
                {
                    showCollaboratorList &&
                    <DialogPanel onClick={this.props.onSelected}>
                        <DialogTitle><Icon>person_add_alt_1</Icon>Who has access?</DialogTitle>
                        <Loadable isLoading={isLoading}>
                            <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                                <DialogContent>
                                    <PermissionsList
                                        orgId={presentation.get("orgId")}
                                        refreshOnMount={true}
                                    />
                                </DialogContent>
                                <DialogActions
                                    style={{
                                        flexDirection: "column",
                                        justifyContent: "center",
                                    }}
                                >
                                    <BlueButton
                                        size="large"
                                        onClick={() => this.handleToggleCollaboratorList(false)}
                                        color="primary"
                                    >Invite new collaborators</BlueButton>
                                </DialogActions>
                            </Expander>
                        </Loadable>
                    </DialogPanel>
                }
            </Fragment>
        );
    }
}

